import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import KPICard from '../analytics-dashboard/components/KPICard';
import DateRangeFilter from '../analytics-dashboard/components/DateRangeFilter';
import PerformanceMetricsGrid from './components/PerformanceMetricsGrid';
import ProductivityTrendsChart from './components/ProductivityTrendsChart';
import RolePerformanceCards from './components/RolePerformanceCards';
import CommissionTracker from './components/CommissionTracker';
import StaffRankingTable from './components/StaffRankingTable';

const StaffPerformanceAnalytics = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'manager';
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('30days');

  const overviewKPIs = [
  {
    title: 'Average Staff Performance',
    value: '87.5%',
    change: '+4.2%',
    changeType: 'positive',
    icon: 'Users',
    iconColor: 'var(--color-primary)',
    trend: 'up'
  },
  {
    title: 'Total Commission Earned',
    value: '$45,230',
    change: '+12.8%',
    changeType: 'positive',
    icon: 'DollarSign',
    iconColor: 'var(--color-success)',
    trend: 'up'
  },
  {
    title: 'Check-in Efficiency',
    value: '94.2%',
    change: '+2.1%',
    changeType: 'positive',
    icon: 'CheckCircle',
    iconColor: 'var(--color-accent)',
    trend: 'up'
  },
  {
    title: 'Member Satisfaction',
    value: '4.6/5',
    change: '+0.3',
    changeType: 'positive',
    icon: 'Star',
    iconColor: 'var(--color-warning)',
    trend: 'up'
  }];


  const performanceMetrics = [
  {
    category: 'Front Desk Staff',
    metrics: [
    { label: 'Check-ins Processed', value: 2847, target: 2500, unit: 'count' },
    { label: 'Average Processing Time', value: 45, target: 60, unit: 'seconds' },
    { label: 'Member Service Rating', value: 4.7, target: 4.5, unit: 'rating' },
    { label: 'Issue Resolution Rate', value: 96, target: 90, unit: 'percentage' }]

  },
  {
    category: 'Personal Trainers',
    metrics: [
    { label: 'Active Clients', value: 187, target: 150, unit: 'count' },
    { label: 'Session Completion Rate', value: 98, target: 95, unit: 'percentage' },
    { label: 'Client Satisfaction', value: 4.8, target: 4.5, unit: 'rating' },
    { label: 'Sales Conversion Rate', value: 34, target: 30, unit: 'percentage' }]

  },
  {
    category: 'Maintenance Staff',
    metrics: [
    { label: 'Equipment Repairs', value: 42, target: 50, unit: 'count' },
    { label: 'Average Response Time', value: 18, target: 30, unit: 'minutes' },
    { label: 'Preventive Maintenance', value: 95, target: 90, unit: 'percentage' },
    { label: 'Facility Cleanliness Score', value: 4.9, target: 4.5, unit: 'rating' }]

  },
  {
    category: 'Management',
    metrics: [
    { label: 'Team Performance Score', value: 89, target: 85, unit: 'percentage' },
    { label: 'Staff Retention Rate', value: 92, target: 85, unit: 'percentage' },
    { label: 'Budget Adherence', value: 97, target: 95, unit: 'percentage' },
    { label: 'Goal Achievement', value: 88, target: 80, unit: 'percentage' }]

  }];


  const productivityData = [
  { week: 'Week 1', frontDesk: 85, trainers: 88, maintenance: 82, management: 90 },
  { week: 'Week 2', frontDesk: 87, trainers: 90, maintenance: 85, management: 89 },
  { week: 'Week 3', frontDesk: 89, trainers: 92, maintenance: 87, management: 91 },
  { week: 'Week 4', frontDesk: 91, trainers: 94, maintenance: 89, management: 93 }];


  const commissionData = [
  {
    staffId: 1,
    name: 'Marcus Thompson',
    role: 'Personal Trainer',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_183f5ab63-1763296652710.png',
    avatarAlt: 'Professional headshot of African American male trainer with short black hair wearing navy blue athletic shirt',
    personalTraining: 8450,
    retailSales: 1230,
    membershipReferrals: 2100,
    totalCommission: 11780,
    goalProgress: 94
  },
  {
    staffId: 2,
    name: 'Emily Chen',
    role: 'Personal Trainer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15780cdc0-1763299445992.png",
    avatarAlt: 'Professional headshot of Asian female trainer with long black hair in ponytail wearing teal athletic top',
    personalTraining: 9200,
    retailSales: 890,
    membershipReferrals: 1800,
    totalCommission: 11890,
    goalProgress: 98
  },
  {
    staffId: 3,
    name: 'Sarah Johnson',
    role: 'Front Desk Manager',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_112fb449f-1763299843776.png",
    avatarAlt: 'Professional headshot of Caucasian female staff with blonde hair in bun wearing purple athletic top',
    personalTraining: 0,
    retailSales: 3450,
    membershipReferrals: 3200,
    totalCommission: 6650,
    goalProgress: 88
  },
  {
    staffId: 4,
    name: 'David Rodriguez',
    role: 'Personal Trainer',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_16d452239-1763299445724.png',
    avatarAlt: 'Professional headshot of Hispanic male trainer with short brown hair wearing red athletic shirt',
    personalTraining: 7300,
    retailSales: 1120,
    membershipReferrals: 1310,
    totalCommission: 9730,
    goalProgress: 82
  }];


  const staffRankings = [
  {
    rank: 1,
    name: 'Emily Chen',
    role: 'Personal Trainer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10c9f2490-1763293752822.png",
    avatarAlt: 'Professional headshot of Asian female trainer with long black hair in ponytail wearing teal athletic top',
    performanceScore: 96,
    checkIns: 0,
    memberRating: 4.9,
    tasksCompleted: 142,
    improvement: '+8%'
  },
  {
    rank: 2,
    name: 'Marcus Thompson',
    role: 'Personal Trainer',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_183f5ab63-1763296652710.png',
    avatarAlt: 'Professional headshot of African American male trainer with short black hair wearing navy blue athletic shirt',
    performanceScore: 94,
    checkIns: 0,
    memberRating: 4.8,
    tasksCompleted: 138,
    improvement: '+6%'
  },
  {
    rank: 3,
    name: 'Sarah Johnson',
    role: 'Front Desk Manager',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_112fb449f-1763299843776.png",
    avatarAlt: 'Professional headshot of Caucasian female staff with blonde hair in bun wearing purple athletic top',
    performanceScore: 92,
    checkIns: 1847,
    memberRating: 4.7,
    tasksCompleted: 156,
    improvement: '+5%'
  },
  {
    rank: 4,
    name: 'David Rodriguez',
    role: 'Personal Trainer',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_16d452239-1763299445724.png',
    avatarAlt: 'Professional headshot of Hispanic male trainer with short brown hair wearing red athletic shirt',
    performanceScore: 89,
    checkIns: 0,
    memberRating: 4.6,
    tasksCompleted: 128,
    improvement: '+4%'
  },
  {
    rank: 5,
    name: 'Jennifer Lee',
    role: 'Front Desk Staff',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_15780cdc0-1763299445992.png',
    avatarAlt: 'Professional headshot of Asian female staff with long black hair wearing teal athletic top',
    performanceScore: 87,
    checkIns: 1623,
    memberRating: 4.5,
    tasksCompleted: 134,
    improvement: '+3%'
  }];


  const roleFilters = [
  { value: 'all', label: 'All Staff', icon: 'Users' },
  { value: 'trainer', label: 'Trainers', icon: 'Dumbbell' },
  { value: 'staff', label: 'Front Desk', icon: 'UserCheck' },
  { value: 'maintenance', label: 'Maintenance', icon: 'Wrench' },
  { value: 'manager', label: 'Management', icon: 'Briefcase' }];


  const handleDateRangeChange = (range) => {
    setSelectedPeriod(range);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-3">
            Staff Performance Analytics
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Comprehensive workforce evaluation and productivity insights
          </p>
        </div>

        <div className="mb-6 md:mb-8">
          <DateRangeFilter onFilterChange={handleDateRangeChange} />
        </div>

        <div className="flex items-center gap-2 mb-6 overflow-x-auto custom-scrollbar pb-2">
          {roleFilters?.map((filter) =>
          <button
            key={filter?.value}
            onClick={() => setSelectedRole(filter?.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap ${
            selectedRole === filter?.value ?
            'bg-primary text-primary-foreground' :
            'bg-muted text-foreground hover:bg-muted/80'}`
            }>

              <Icon name={filter?.icon} size={16} />
              {filter?.label}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {overviewKPIs?.map((kpi, index) =>
          <KPICard key={index} {...kpi} />
          )}
        </div>

        <div className="mb-6 md:mb-8">
          <PerformanceMetricsGrid metrics={performanceMetrics} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <ProductivityTrendsChart data={productivityData} />
          <RolePerformanceCards selectedRole={selectedRole} />
        </div>

        <div className="mb-6 md:mb-8">
          <CommissionTracker data={commissionData} />
        </div>

        <div className="mb-6 md:mb-8">
          <StaffRankingTable data={staffRankings} />
        </div>
      </main>
    </div>);

};

export default StaffPerformanceAnalytics;